/**
 * The 422 a cached menu makes routine, and how a screen points at it.
 *
 * `order-pricing.ts` raises `UnprocessableEntityException('"Espresso" is sold
 * out')`, and `assertMenuWindowsOpen` raises the same status for an item whose
 * menu is outside its schedule window right now. Neither is a bug in the cache:
 * sold-out flags and schedule windows move without a client hearing about it,
 * only the server knows, and it says so at the one moment that matters. A
 * screen that renders that as "action failed" makes an operator start again
 * instead of swapping one line.
 *
 * `Redux/Slices/soldOutRefusal.test.ts` traces the sentence through the real
 * write path and is the specification for the behaviour; this module is the
 * half of it every ordering screen shares, so `QuickOrderDialog` and the
 * register cannot drift apart on which line a refusal is about.
 */

/** The API quotes the item name, and a variant line reads `Item — Variant`. */
const QUOTED = /"([^"]+)"/;

/** The name the server's sentence is about, or null when it names none. */
export function refusedName(message: string): string | null {
  return QUOTED.exec(message)?.[1] ?? null;
}

/**
 * The line a refusal is complaining about.
 *
 * Exact match first, then prefix: the message names the item, and a line whose
 * display name carries its size (`Latte — Large`) still starts with it. Answers
 * null rather than guessing when nothing matches — a highlight on the wrong
 * line is worse than none, because the operator removes the wrong thing.
 */
export function refusedLine<T extends { name: string }>(message: string, lines: T[]): T | null {
  const quoted = refusedName(message);
  if (!quoted) return null;
  return (
    lines.find((line) => line.name === quoted) ??
    lines.find((line) => quoted.startsWith(line.name)) ??
    null
  );
}
